import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Vote, Crown, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { api, pad, fmtDate, money, formatApiError, startCheckout } from "../lib/api";
import { PLAN_LABEL, partySize } from "../lib/tiers";
import { PoolTracker } from "./PoolTracker";
import { JoinDialog } from "./JoinDialog";
import { OfferCard } from "./OfferCard";
import { SchoolLine } from "./SchoolLine";
import { ExtraFundCard } from "./ExtraFundCard";
import { BuyNowCard } from "./BuyNowCard";
import { PlanningCard } from "./PlanningCard";
import { AutopayCard } from "./AutopayCard";

const STATUS = {
  active: { label: "Active", cls: "bg-emerald-500/15 text-emerald-400" },
  none: { label: "Not in this line", cls: "bg-white/10 text-slate-300" },
  lapsed: { label: "Lapsed — off the list", cls: "bg-red-500/15 text-red-400" },
  winner: { label: "Turn complete", cls: "bg-[#38BDF8]/15 text-[#38BDF8]" },
};

const Done = ({ award }) => (
  <div data-testid="winner-banner" className="mt-6 lux-card p-6 border-[#38BDF8]/40 bg-[#38BDF8]/[0.07] flex items-center gap-4">
    {award.status === "donated" ? <Heart className="w-7 h-7 text-rose-300" /> : <Crown className="w-7 h-7 text-[#38BDF8]" />}
    <p>
      {award.status === "donated"
        ? <>You gave <b>{money(award.charity_amount)}</b> in trade-school scholarships to {award.charity_name}. Thank you!</>
        : <>You won <b>{award.vacation_title}</b> in {award.vacation_location}! {award.charity_name && <>{money(award.charity_amount)} went to scholarships at {award.charity_name}.</>} The club will contact you to book.</>}
    </p>
  </div>
);

export const MembershipPanel = ({ tier, data, ts, promo, charities, awards, onDone }) => {
  const [open, setOpen] = useState(false);
  const m = data.membership;
  const status = m ? m.status : "none";
  const active = status === "active";
  const done = awards.find((a) => a.tier === tier && (a.status === "accepted" || a.status === "donated"));
  const label = active ? "Renew early" : status === "none" ? "Join this line" : "Rejoin at the back";

  const pay = async (opts) => {
    try { await startCheckout({ kind: "membership", tier, ...opts }); } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel? You'll lose your place in this line.")) return;
    try { await api.post(`/memberships/${tier}/cancel`); toast("Membership cancelled"); onDone(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={`membership-panel-${tier}`}>
      {data.pending_offer && <OfferCard offer={data.pending_offer} charities={charities} defaultSchool={m?.school_id} settings={ts} onDone={onDone} />}
      {!data.pending_offer && done && status !== "active" && <Done award={done} />}
      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 lux-card p-8 md:p-10 relative overflow-hidden">
          <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(244,63,94,0.18), transparent 70%)" }} />
          <div className="flex flex-wrap gap-2 items-center">
            <span data-testid="member-status-badge" className={`text-[11px] font-mono-lux uppercase tracking-wider px-3 py-1 rounded-full ${STATUS[status].cls}`}>{STATUS[status].label}</span>
            {m && <span data-testid="member-level" className={`text-[11px] font-mono-lux uppercase tracking-wider px-3 py-1 rounded-full ${m.level === "premium" ? "bg-gradient-to-r from-[#A855F7] to-[#F43F5E] text-white" : "bg-white/5 text-slate-300"}`}>{m.level}</span>}
            {m && <span data-testid="member-plan" className="text-[11px] font-mono-lux uppercase tracking-wider px-3 py-1 rounded-full bg-white/5 text-slate-300">{PLAN_LABEL[m.plan]}{m.extra_people ? ` +${m.extra_people}` : ""} · {partySize(m.plan, m.extra_people)} travelers</span>}
          </div>
          <p className="eyebrow mt-8">Queue position</p>
          <p data-testid="member-queue-position" className="font-mono-lux text-6xl md:text-7xl mt-2 text-[#38BDF8]">{pad(data.position)}</p>
          <p className="text-slate-400 text-sm mt-2">{active ? `of ${data.queue_size} paid members in ${ts.label}` : "You're not in this line right now."}</p>
          {active && data.est_wait_months != null && (
            <p data-testid="member-est-wait" className="text-sm text-slate-300 mt-4">
              Estimated wait: <span className="font-mono-lux text-[#38BDF8]">{data.est_wait_months < 1 ? "under a month" : `~${data.est_wait_months} months`}</span>
              <span className="block text-xs text-slate-500 mt-1">Estimate only, based on today's members and payments. Trips are not guaranteed.</span>
              <span data-testid="member-self-funded" className="block text-xs text-amber-200/80 mt-1">
                If sign-ups stopped: about {data.pricing?.self_funded_months} months of your own payments to cover your trip.
              </span>
            </p>
          )}
          {active && <PlanningCard p={data.planning} />}
          {active && <p data-testid="member-paid-until" className="text-sm text-slate-300 mt-6">Paid through <span className="font-mono-lux">{fmtDate(m.paid_until)}</span>{m.autopay && " · autopay on"}</p>}
          {active && m.payment_failed_at && <p data-testid="member-payment-failed" className="mt-4 flex items-center gap-2 text-sm text-red-300"><AlertTriangle className="w-4 h-4" />Your last auto-pay was declined. Stripe will retry, or renew manually to keep your spot.</p>}
          {m && <SchoolLine tier={tier} m={m} charities={charities} onDone={onDone} />}
          {m?.companion_name && <p className="text-xs text-slate-500 mt-2">Traveling with: {m.companion_name}</p>}
          {m?.notes && <p className="text-xs text-slate-500 mt-1">{ts.notes_label}: {m.notes}</p>}
          {status === "lapsed" && <p className="mt-6 flex items-center gap-2 text-sm text-red-300"><AlertTriangle className="w-4 h-4" />Renewing places you at the back of the line.</p>}
          <div className="mt-8 flex flex-wrap gap-3">
            {!(active && m.autopay) && <button data-testid="member-pay-btn" onClick={() => setOpen(true)} className="btn-gold px-6 py-3">{label}</button>}
            {active && <button data-testid="member-cancel-btn" onClick={cancel} className="btn-ghost px-6 py-3 text-sm">Cancel</button>}
            {active && (
              <Link to="/vacations" data-testid="member-vote-link" className="btn-ghost px-6 py-3 text-sm inline-flex items-center gap-2">
                <Vote className="w-4 h-4" />{m.vote_vacation_id ? "Change your vote" : "Vote next trip"}
              </Link>
            )}
          </div>
        </div>
        <div className="lg:col-span-5 space-y-6">
          <PoolTracker stats={ts} compact />
          {active && <AutopayCard m={m} ts={ts} pricing={data.pricing} onDone={onDone} />}
        </div>
      </div>
      {active && <div className="mt-6 grid lg:grid-cols-12 gap-6"><div className="lg:col-span-5"><ExtraFundCard m={m} /></div>
        {!data.pending_offer && <div className="lg:col-span-7"><BuyNowCard m={m} ts={ts} /></div>}</div>}
      {open && <JoinDialog open={open} onOpenChange={setOpen} ts={ts} membership={m} onConfirm={pay} />}
    </div>
  );
};
